"use client"

import { useCallback, useEffect, useMemo } from "react"

import type { RunRowLike } from "@/types/RunRow"
import type { Message, Transcript } from "@/types/Transcript"

import { groupSegmentsIntoTurns } from "../utils/groupSegmentsIntoTurns"
import { isSimulationLive } from "../utils/isSimulationLive"
import {
  AGENT,
  type SimulationRoomEvent,
  type SimulationRoomStatus,
} from "./simulationRoomTypes"
import { useSimulationRoom } from "./useSimulationRoom"

export interface LiveSimulationTranscript {
  /** Whether the row is currently live (and the hook enabled). */
  isLive: boolean
  roomStatus: SimulationRoomStatus
  /** Live audio control. Sessions start muted; unmuting is the user gesture. */
  isMuted: boolean
  toggleMute: () => void
  /**
   * Restart a session stuck in `error` or `stalled` while the row is still
   * live. Auto-join only starts from `idle`, so without this a transient
   * connect failure would dead-end the transcript for the rest of the call.
   */
  retryConnection: () => void
  /** Seconds since the simulation started, from the room's corrected clock. */
  elapsedSeconds: number | null
  /** Speaker activity levels (0..1) keyed by track name (AGENT/PERSONA). */
  levels: Record<string, number>
  /**
   * The live turns, tool calls, and websocket events in the stored-transcript
   * shape, so the regular conversation viewer renders them unchanged. Persona
   * speech maps to `role: "user"` — in stored transcripts "user" is the Coval
   * persona.
   */
  liveTranscript: Transcript
}

/**
 * A transcript event in the shared `Message` shape: a tool call keeps its
 * public arguments and its owner's side of the chat, and a websocket event
 * keeps its type and name. An event's offsets are final on arrival, so its
 * timestamp needs no settling. A role this client does not know is dropped.
 */
function eventToMessage(event: SimulationRoomEvent): Message | null {
  const stamped =
    event.startOffsetSeconds !== undefined &&
    event.endOffsetSeconds !== undefined
  const offsets = stamped
    ? { beginning: event.startOffsetSeconds, end: event.endOffsetSeconds }
    : {}
  if (event.role === "websocket_event") {
    return {
      role: "websocket_event",
      content: event.content,
      name: event.name,
      ...offsets,
    }
  }
  if (event.role === "tool") {
    return {
      role: "tool",
      content: event.content,
      name: event.name,
      tool_call_owner: event.toolCallOwner === AGENT ? "assistant" : "user",
      ...offsets,
    }
  }
  return null
}

/**
 * A live transcript for one simulation, in the shared `Transcript` shape.
 *
 * Joins the simulation's room automatically while the row is live — with
 * audio muted from the start, so the auto-join breaks no autoplay rule; the
 * transcript arrives over the data channel regardless. This is the one
 * sanctioned auto-starting mount (see the module README): everything audible
 * still begins with a user gesture.
 *
 * The caller must key its mount on the simulation id — the room inside is
 * per-mount, and the one-room-at-a-time rule relies on React tearing the old
 * mount down when the simulation changes.
 */
export function useLiveSimulationTranscript(
  row: RunRowLike | null | undefined,
  {
    enabled = true,
    startMuted = true,
  }: { enabled?: boolean; startMuted?: boolean } = {},
): LiveSimulationTranscript {
  const simulationId = row?.simulation_output_id ?? ""
  const isLive = Boolean(
    enabled && simulationId && row && isSimulationLive(row),
  )
  const room = useSimulationRoom(simulationId, { isLive, startMuted })
  const { start, stop, status, segments, events } = room

  useEffect(() => {
    if (isLive && status === "idle") {
      start()
    }
  }, [isLive, start, status])

  // The row stopped being live while the session never reached its own
  // completion (e.g. the poll noticed the end before the room's terminal
  // envelope arrived) — release the connection rather than waiting to stall.
  useEffect(() => {
    if (!isLive && status !== "idle" && status !== "complete") {
      stop()
    }
  }, [isLive, status, stop])

  const retryConnection = useCallback(() => {
    if (isLive && (status === "error" || status === "stalled")) {
      start()
    }
  }, [isLive, start, status])

  const liveTranscript = useMemo<Transcript>(() => {
    const eventMessages = events.flatMap((event) => {
      const message = eventToMessage(event)
      return message ? [{ orderSeq: event.orderSeq, message }] : []
    })
    const orderedEntries = [
      ...segments.map((segment) => ({
        kind: "segment" as const,
        orderSeq: segment.orderSeq,
        segment,
      })),
      ...eventMessages.map((event) => ({
        kind: "event" as const,
        orderSeq: event.orderSeq,
      })),
    ].sort((a, b) => a.orderSeq - b.orderSeq)
    const turns: ReturnType<typeof groupSegmentsIntoTurns> = []
    let pendingSegments: typeof segments = []
    const flushSegments = () => {
      turns.push(...groupSegmentsIntoTurns(pendingSegments))
      pendingSegments = []
    }
    for (const entry of orderedEntries) {
      if (entry.kind === "segment") {
        pendingSegments.push(entry.segment)
      } else {
        flushSegments()
      }
    }
    flushSegments()

    const turnMessages = turns.map((turn, index) => {
      // The simulator bounds persona segments with the bot speech window
      // (COVAL-5499), so the turn offsets are real utterance spans in
      // seconds since the call started — the same scale `beginning`/`end`
      // carry on stored transcripts. The active (last) turn's span is still
      // moving — the agent's end grows with each recognized phrase, and the
      // persona's end is corrected when its audio finishes — so its
      // timestamp is withheld until the turn settles rather than rendered
      // as a number that keeps changing mid-speech.
      const settled = index < turns.length - 1 || status === "complete"
      // Settled by position is not enough: a persona span's corrected end can
      // still be in flight when the next turn starts, so the stamp also waits
      // for the turn's final revision (is_final marks the point where a
      // segment stops changing). The protocol also permits segments without
      // offsets; a turn missing either bound gets no timestamp rather than a
      // fabricated 0:00.
      const stamped =
        settled &&
        turn.isFinal &&
        turn.startOffsetSeconds !== undefined &&
        turn.endOffsetSeconds !== undefined
      return {
        orderSeq: turn.orderSeq,
        message: {
          role:
            turn.speaker === AGENT ? ("assistant" as const) : ("user" as const),
          content: turn.text,
          ...(stamped && {
            beginning: turn.startOffsetSeconds,
            end: turn.endOffsetSeconds,
          }),
        },
      }
    })
    // Tool calls and websocket events interleave with speech by position and
    // terminate the surrounding speech turns. The simulator assigns sequence
    // numbers to both from one counter, and orderSeq preserves each entry's
    // first emission through replays.
    return [...turnMessages, ...eventMessages]
      .sort((a, b) => a.orderSeq - b.orderSeq)
      .map((entry) => entry.message)
  }, [events, segments, status])

  return {
    isLive,
    roomStatus: status,
    isMuted: room.isMuted,
    toggleMute: room.toggleMute,
    retryConnection,
    elapsedSeconds: room.elapsedSeconds,
    levels: room.levels,
    liveTranscript,
  }
}
